import React,{useEffect,useMemo,useRef,useState} from 'react'
import axios from 'axios'

import Panel from './ui/Panel'
import Btn from './ui/Btn'
import VideoModal from './ui/VideoModal'
import {videoUrl,posterUrl} from './ui/videoSource'
import FileUpload from './FileUpload'
import './protected/settings.css'

/*
 * The video library.
 *
 * Playback used to be a bootstrap modal wrapping the app's own hls.js player
 * (HLSPlayer.js). It is now the shared VideoModal, so this screen gets the same
 * surface as the rest of the product: portrait-aware framing, playback speed,
 * frame stepping and next / previous through the page.
 *
 * Every URL goes through ui/videoSource, which strips a stored extension and
 * walks 1080_{id}.mp4 -> {id}.smil -> 720_{id}.mp4, so a clip whose .smil was
 * never generated still plays here.
 *
 * Not one axios call, payload or response check changed.
 */

const PAGE=10

/* ------------------------------------------------------------------
   Icons — stroke: currentColor so they inherit the token colour.
   ------------------------------------------------------------------ */
const IconX=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
)

const IconSearch=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7"/>
    <path d="m20 20-3.5-3.5"/>
  </svg>
)

const IconPlay=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7Z"/></svg>
)

/* 92 -> "1:32" · 5 -> "0:05" */
const asDuration=(value)=>{
  const total=parseInt(value)
  if (!(total>0)) return '0:00'
  const min=parseInt(total/60)
  const sec=total-min*60
  return min+':'+(sec<10 ? '0'+sec : ''+sec)
}

/* the upload stamp, in the same shape the screen has always shown */
const asDate=(stamp)=>{
  const ms=parseInt(stamp)
  if (!(ms>0)) return '—'
  return new Date(ms).toString().split('GMT')[0].trim()
}

const byNewest=(a,b)=>((a.Timestamp<b.Timestamp) ? 1 : -1)

export default function Videos() {

    const [videos,setVideos]=useState([])
    /* true from the first paint: loadVideos() fires on mount */
    const [isLoading,setIsLoading]=useState(true)
    const [failed,setFailed]=useState(false)
    const [fn,setFn]=useState(0)
    /* the friendly name being typed, kept out of the list until it is saved */
    const [draft,setDraft]=useState('')
    const [newVideo,setNewVideo]=useState('')
    const [startIndex,setStartIndex]=useState(0)
    const [q,setQ]=useState('')

    /* which entry the shared player is showing; null while it is closed */
    const [playing,setPlaying]=useState(null)

    /* inline feedback — this is what replaced the alert() calls */
    const [note,setNote]=useState(null)
    /* {id, name} while a delete is being confirmed */
    const [pending,setPending]=useState(null)
    const [askRestart,setAskRestart]=useState(false)

    /* the processing poll, so it can be stopped when the screen goes away */
    const pollRef=useRef(null)

    useEffect(()=>{
        loadVideos()
        return ()=>{
          if (pollRef.current) clearInterval(pollRef.current)
        }
    },[])

    /* Escape closes whichever confirmation is open. */
    useEffect(()=>{
      if (!pending && !askRestart) return undefined
      const onKey=(ev)=>{
        if (ev.key!=='Escape') return
        setPending(null)
        setAskRestart(false)
      }
      document.addEventListener('keydown',onKey)
      return ()=>document.removeEventListener('keydown',onKey)
    },[pending,askRestart])

    const loadVideos=()=>{
        setIsLoading(true)
        setFailed(false)
        axios.get(process.env.REACT_APP_VIDEOSERVER_API+'upload/'+process.env.REACT_APP_NAME)
        .then(res=>{
            setVideos(res.data.sort(byNewest))
            setIsLoading(false)
        }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            setNote({tone:'crit',text:'Η λίστα βίντεο δεν φορτώθηκε. Ελέγξτε τη σύνδεση και δοκιμάστε ξανά.'})
            console.log(err)
        })
    }

    const renVideo=(vid,ffn)=>{

        if (ffn){
            const formData=new FormData()
            formData.append('fn',vid)
            formData.append('ffn',ffn)
            axios.post(process.env.REACT_APP_VIDEOSERVER_API+'vjson',formData,{})
            .then(res=>{
               if (res.data.err==0){
                    setVideos(list=>list.map(item=>(
                      item.id==vid ? {...item,friendlyName:ffn} : item
                    )))
                    setFn(0)
                    setDraft('')
                    setNote({tone:'good',text:'Η φιλική ονομασία αποθηκεύτηκε.'})
               }else{
                    setNote({tone:'crit',text:'Η ονομασία δεν αποθηκεύτηκε. Δοκιμάστε ξανά.'})
               }
            }).catch(err=>{
                setNote({tone:'crit',text:'Η ονομασία δεν αποθηκεύτηκε. Δοκιμάστε ξανά.'})
                console.log(err)
            })
        }else{
            setNote({tone:'crit',text:'Συμπληρώστε μια φιλική ονομασία πριν την αποθήκευση.'})
        }

    }

    const startEdit=(item)=>{
        setNote(null)
        setFn(item.id)
        setDraft(!item.friendlyName ? '' : item.friendlyName)
    }

    const cancelEdit=()=>{
        setFn(0)
        setDraft('')
    }

    const addNewVideo=(vidObj)=>{
        setVideos([...videos,vidObj].sort(byNewest))

        setNewVideo(vidObj.id)
        setStartIndex(0)

        if (pollRef.current) clearInterval(pollRef.current)

        var checkStatus=setInterval(function(){
            const url=process.env.REACT_APP_VIDEOSERVER_API+'upload/'+process.env.REACT_APP_NAME+'_'+vidObj.id
            axios.get(url)
            .then(res=>{
                if (res.data[0].status==1){
                    loadVideos()
                    setNewVideo('')
                    clearInterval(checkStatus)
                    pollRef.current=null
                }
            })
        }, 3000);

        pollRef.current=checkStatus
    }

    const delVideo=(vid)=>{
            axios.delete(process.env.REACT_APP_VIDEOSERVER_API+'upload/'+vid)
            .then(res=>{

                axios.delete(process.env.REACT_APP_API_SERVER+'admin/videos/'+vid,{
                    headers: {
                        'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
                .then(res=>{
                    if (res.data.err==0){
                        setNote({tone:'good',text:'Το βίντεο διαγράφηκε από όλα τα σημεία'})
                    }else{
                        setNote({tone:'crit',text:'Η ενημέρωση δεν έγινε.'})
                    }
                })

                setVideos(list=>[...list].filter(item=>item.id!=vid).sort(byNewest))
                setStartIndex(0)
                setPending(null)

            }).catch(err=>{
                setPending(null)
                setNote({tone:'crit',text:'Η διαγραφή απέτυχε: '+err.message})
            })
    }

    const restartService=()=>{
            setAskRestart(false)
            axios.get(process.env.REACT_APP_VIDEOSERVER_API+'restart')
            .then(res=>{
                setNote({tone:'info',text:res.data.msg})
            }).catch(err=>{
                setNote({tone:'crit',text:'Η επανεκκίνηση απέτυχε.'})
            })
    }

    /* ---------------------------------------------------------------
       Derived view: search wins over paging, exactly as it did before.
       --------------------------------------------------------------- */
    const matches=useMemo(()=>{
      const needle=q.trim()
      if (!needle) return videos
      return videos.filter(item=>{
        if (!item.id) return false
        const id=String(item.id)
        const name=String(item.friendlyName||'')
        return id.indexOf(needle)>-1 || name.indexOf(needle)>-1
      })
    },[videos,q])

    const searching=q.trim().length>0

    const shown=useMemo(()=>(
      searching ? matches : matches.slice(startIndex,startIndex+PAGE)
    ),[matches,searching,startIndex])

    const pagesArray=useMemo(()=>{
      const pages=Math.ceil(videos.length/PAGE)
      const out=[]
      for (let i=0;i<pages;i+=1){ out.push(i*PAGE) }
      return out
    },[videos.length])

    /* what the shared player walks through: everything on screen that has
       finished processing */
    const playable=useMemo(()=>(
      shown
        .filter(item=>item.id && item.id!=newVideo)
        .map(item=>({
          fileId:item.id,
          name:!item.friendlyName ? item.id : item.friendlyName,
          uuid:'',
          subtitle:asDate(item.Timestamp)
        }))
    ),[shown,newVideo])

    const openPlayer=(id)=>{
      const at=playable.findIndex(entry=>entry.fileId==id)
      setPlaying(at<0 ? 0 : at)
    }

    const total=videos.length
    const last=Math.min(startIndex+PAGE,total)

    return (
        <div className="st-page">

          {note
            ? <div className="st-note" data-tone={note.tone} role="alert">
                <span className="st-note-text">{note.text}</span>
                <button type="button" className="st-note-x" aria-label="Κλείσιμο" onClick={()=>setNote(null)}>
                  <IconX/>
                </button>
              </div>
            : null}

          {newVideo.length==0
            ? <Panel title="Ανέβασμα Βίντεο" subtitle="MP4 μόνο — το αρχείο περνά από τον video server.">
                <FileUpload addNewVideo={addNewVideo} nvl={newVideo.length} type="1"/>
              </Panel>
            : <Panel title="Ανέβασμα Βίντεο" subtitle="Σε εξέλιξη">
                <div className="st-stack">
                  <div className="st-vproc"><i></i>Processing video, please wait...</div>
                  <div className="st-hint">
                    Η λίστα ανανεώνεται μόνη της μόλις ολοκληρωθεί η επεξεργασία.
                  </div>
                </div>
              </Panel>}

          <Panel
            title="Λίστα Βίντεο"
            subtitle={
              isLoading
                ? 'Φόρτωση…'
                : total===0
                  ? 'Κανένα βίντεο'
                  : total+' βίντεο'
            }
            actions={
              <>
                {total>0
                  ? <span className="st-search">
                      <IconSearch/>
                      <input
                        className="st-input"
                        type="search"
                        value={q}
                        placeholder="Search..."
                        aria-label="Αναζήτηση βίντεο"
                        onChange={(e)=>setQ(e.target.value)}
                      />
                    </span>
                  : null}
                <Btn className="st-crit" onClick={()=>setAskRestart(true)}>Επανεκκίνηση υπηρεσίας</Btn>
              </>
            }
          >
            {isLoading
              ? <div className="st-sk" aria-hidden="true">
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                </div>
              : failed
                ? <div className="st-empty">
                    <div className="st-empty-t">Τα βίντεο δεν φορτώθηκαν</div>
                    <div className="st-empty-s">
                      Ο video server δεν απάντησε. Δεν χάθηκε τίποτα — δοκιμάστε ξανά.
                    </div>
                    <div className="st-empty-a">
                      <Btn variant="accent" onClick={loadVideos}>Δοκιμή ξανά</Btn>
                    </div>
                  </div>
                : total===0
                  ? <div className="st-empty">
                      <div className="st-empty-t">Δεν υπάρχουν βίντεο</div>
                      <div className="st-empty-s">
                        Ανεβάστε ένα αρχείο MP4 από το πάνω πλαίσιο. Μόλις ολοκληρωθεί η επεξεργασία
                        στον video server, το βίντεο εμφανίζεται εδώ.
                      </div>
                    </div>
                  : shown.length===0
                    ? <div className="st-empty">
                        <div className="st-empty-t">Καμία αντιστοιχία για «{q.trim()}»</div>
                        <div className="st-empty-s">
                          Υπάρχουν {total} βίντεο. Καθαρίστε την αναζήτηση για να τα δείτε όλα.
                        </div>
                        <div className="st-empty-a">
                          <Btn onClick={()=>setQ('')}>Καθαρισμός αναζήτησης</Btn>
                        </div>
                      </div>
                    : <>
                        <div className="st-vlist">
                          {shown.map(item=>{
                            if (!item.id) return null

                            const processing=newVideo==item.id
                            const editing=fn==item.id
                            const named=!!item.friendlyName

                            return (
                              <div className="st-vrow" key={item.id}>

                                <div>
                                  {processing
                                    ? <div className="st-vthumb" aria-hidden="true"></div>
                                    : <button
                                        type="button"
                                        className="st-vthumb"
                                        onClick={()=>openPlayer(item.id)}
                                        aria-label={'View Video — '+(named ? item.friendlyName : item.id)}
                                      >
                                        <img src={posterUrl(item.id)} alt="" loading="lazy"/>
                                        <span className="st-vplay"><span><IconPlay/></span></span>
                                      </button>}
                                </div>

                                <div className="st-vmeta">

                                  {processing
                                    ? <div className="st-vproc"><i></i>Processing video, please wait...</div>
                                    : null}

                                  {editing
                                    ? <div className="st-vedit">
                                        <div className="st-field">
                                          <label className="mono st-label" htmlFor={'st-fn-'+item.id}>
                                            Φιλική Ονομασία
                                          </label>
                                          <input
                                            id={'st-fn-'+item.id}
                                            className="st-input"
                                            type="text"
                                            placeholder="Φιλική Ονομασία"
                                            value={draft}
                                            onChange={(e)=>setDraft(e.target.value)}
                                          />
                                        </div>
                                        <div className="st-up-actions">
                                          <Btn variant="accent" onClick={()=>renVideo(item.id,draft)}>Αποθήκευση</Btn>
                                          <Btn onClick={cancelEdit}>Άκυρο</Btn>
                                        </div>
                                      </div>
                                    : <div className="st-vname" data-unnamed={named ? '0' : '1'}>
                                        {named ? item.friendlyName : 'Χωρίς φιλική ονομασία'}
                                      </div>}

                                  <div className="st-vfacts">
                                    <span className="st-vfact">
                                      <span className="mono st-vfact-k">ID</span>
                                      <span className="st-vfact-v">{item.id}</span>
                                    </span>
                                    <span className="st-vfact">
                                      <span className="mono st-vfact-k">Ημερομηνία που Ανέβηκε</span>
                                      <span className="st-vfact-v">{asDate(item.Timestamp)}</span>
                                    </span>
                                    <span className="st-vfact">
                                      <span className="mono st-vfact-k">Διάρκεια</span>
                                      <span className="st-vfact-v">{asDuration(item.duration)}</span>
                                    </span>
                                  </div>

                                  {processing
                                    ? null
                                    : <code className="st-vurl">{videoUrl(item.id)}</code>}

                                </div>

                                <div className="st-vactions">
                                  {processing
                                    ? null
                                    : <>
                                        <Btn onClick={()=>openPlayer(item.id)}>View Video</Btn>
                                        {editing
                                          ? null
                                          : <Btn onClick={()=>startEdit(item)}>Επεξεργασία</Btn>}
                                        <Btn
                                          className="st-crit"
                                          onClick={()=>setPending({id:item.id,name:named ? item.friendlyName : item.id})}
                                        >
                                          Ολική Διαγραφή Βίντεο
                                        </Btn>
                                      </>}
                                </div>

                              </div>
                            )
                          })}
                        </div>

                        {searching
                          ? <div className="st-pager">
                              <span className="st-pager-note">
                                {matches.length} από {total} videos
                              </span>
                            </div>
                          : <div className="st-pager">
                              <div className="st-pages">
                                {pagesArray.map((item,index)=>(
                                  <button
                                    type="button"
                                    className="st-page-btn"
                                    key={index}
                                    aria-current={item==startIndex ? 'true' : 'false'}
                                    onClick={()=>{
                                      setStartIndex(item)
                                      cancelEdit()
                                    }}
                                  >
                                    {item/PAGE+1}
                                  </button>
                                ))}
                              </div>
                              <span className="st-pager-note">
                                {startIndex+1}-{last} of {total} videos
                              </span>
                            </div>}
                      </>}
          </Panel>

          {playing!==null && playable.length>0
            ? <VideoModal
                items={playable}
                index={playing}
                title="Λίστα Βίντεο"
                onIndex={(next)=>setPlaying(next)}
                onClose={()=>setPlaying(null)}
              />
            : null}

          {pending
            ? <div className="st-modal" role="dialog" aria-modal="true" aria-labelledby="st-vid-del">
                <div className="st-modal-panel">
                  <div className="st-modal-head">
                    <div className="st-modal-headline">
                      <div className="st-modal-title" id="st-vid-del">Ολική Διαγραφή Βίντεο</div>
                      <div className="st-modal-sub">Το βίντεο αφαιρείται από τον video server και από όλα τα σημεία της εφαρμογής.</div>
                    </div>
                    <button type="button" className="st-modal-x" aria-label="Κλείσιμο" onClick={()=>setPending(null)}>
                      <IconX/>
                    </button>
                  </div>
                  <div className="st-modal-body">
                    <div className="st-note" data-tone="crit" role="alert">
                      <span className="st-note-text">
                        Θα διαγραφεί τελέιως το βίντεο <b>{pending.name}</b>. Σίγουρα???
                      </span>
                    </div>
                    <div className="st-foot">
                      <Btn onClick={()=>setPending(null)}>Άκυρο</Btn>
                      <Btn className="st-crit" onClick={()=>delVideo(pending.id)}>Ολική Διαγραφή Βίντεο</Btn>
                    </div>
                  </div>
                </div>
              </div>
            : null}

          {askRestart
            ? <div className="st-modal" role="dialog" aria-modal="true" aria-labelledby="st-vid-rs">
                <div className="st-modal-panel">
                  <div className="st-modal-head">
                    <div className="st-modal-headline">
                      <div className="st-modal-title" id="st-vid-rs">Επανεκκίνηση υπηρεσίας</div>
                      <div className="st-modal-sub">Θα γίνει επανεκκίνηση της υπηρεσίας. Σίγουρα?</div>
                    </div>
                    <button type="button" className="st-modal-x" aria-label="Κλείσιμο" onClick={()=>setAskRestart(false)}>
                      <IconX/>
                    </button>
                  </div>
                  <div className="st-modal-body">
                    <div className="st-hint">
                      Όσο διαρκεί η επανεκκίνηση, ο video server δεν εξυπηρετεί ανεβάσματα ούτε αναπαραγωγή.
                    </div>
                    <div className="st-foot">
                      <Btn onClick={()=>setAskRestart(false)}>Άκυρο</Btn>
                      <Btn className="st-crit" onClick={restartService}>Επανεκκίνηση</Btn>
                    </div>
                  </div>
                </div>
              </div>
            : null}

        </div>

    )
}
